import { useEffect, useRef, useState } from 'react';
import { CATALOG_PARTY_SIZE, costNeedsPartyCaveat, isAuditTag } from '@ambit/shared/constants';
import { api, type OutingDetail as Detail } from '../api';

/**
 * Everything the catalog knows about one place.
 *
 * Built on the native `<dialog>` rather than a positioned `<div>`, which buys
 * the whole modal contract for free and correctly: focus is trapped inside,
 * Escape closes, the rest of the page goes inert to assistive tech, and the
 * blurred scrim is a real `::backdrop` element instead of a sibling that has to
 * be kept in sync with z-index. Hand-rolled modals get the focus trap wrong far
 * more often than they get it right.
 */
export function OutingDetailDialog({ id, onClose, onBuild, partySize }: {
  id: string | null;
  onClose: () => void;
  onBuild: (heroId: string, pinnedId?: string) => void;
  /** Null when signed out or when the household has not said its size. */
  partySize: number | null;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Opening and closing is driven by `id`, but the dialog element keeps its own
  // open/closed state — so the two have to be reconciled imperatively. Calling
  // showModal() on an already-open dialog throws, hence the guards.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (id && !el.open) el.showModal();
    if (!id && el.open) el.close();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setDetail(null);
    setError(null);
    api
      .getOuting(id)
      .then((d) => !cancelled && setDetail(d))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Could not load it.'));
    return () => {
      cancelled = true;
    };
  }, [id]);

  return (
    <dialog
      ref={ref}
      // `close` fires for Escape and for backdrop dismissal alike, so parent
      // state stays in step however the dialog was dismissed.
      onClose={onClose}
      // Clicking the backdrop lands on the dialog element itself; clicking any
      // content lands on a descendant. That distinction is the whole test.
      onClick={(e) => e.target === e.currentTarget && ref.current?.close()}
      className="m-auto w-[min(38rem,calc(100vw-2rem))] rounded-lg bg-spruce-900 p-0 text-bone-300 backdrop:bg-spruce-950/70 backdrop:backdrop-blur-sm"
    >
      {error && <p className="p-6 text-sm text-bone-300">{error}</p>}
      {!detail && !error && <p className="p-6 text-sm text-bone-500">Loading…</p>}
      {detail && <Body detail={detail} onBuild={onBuild} onClose={() => ref.current?.close()} />}
    </dialog>
  );
}

function Body({ detail, onBuild, onClose }: {
  detail: Detail;
  onBuild: (heroId: string, pinnedId?: string) => void;
  onClose: () => void;
}) {
  const visits = detail.visitHistory;
  return (
    <div className="max-h-[85vh] overflow-y-auto p-6">
      <header>
        <h2 className="display text-3xl font-bold uppercase leading-none tracking-tight text-bone-100">
          {detail.name}
        </h2>
        <p className="mt-1 text-sm text-bone-500">
          {detail.locality} · {detail.regionName}
        </p>
      </header>

      {/* The reason the dialog exists: 108 of 176 blurbs don't fit on a card. */}
      <p className="mt-4 text-base leading-relaxed text-bone-300">{detail.blurb}</p>

      <dl className="numeric mt-5 grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-4">
        <Fact label="cost" value={detail.costUsd === 0 ? 'free' : `$${detail.costUsd}`} accent={detail.costUsd === 0} />
        <Fact label="hours" value={`${detail.hours}h`} />
        <Fact label="drive" value={`${detail.driveMinutes} min`} />
        <Fact label="effort" value={detail.effort ?? 'none to speak of'} accent={detail.effort === 'strenuous'} />
      </dl>
      {detail.costNote && <p className="mt-2 text-xs text-bone-500">{detail.costNote}</p>}

        {/*
          Only where the number actually moves. A $20 parking fee is $20
          whether three arrive or five, so warning about it would be noise —
          and a caveat that appears on every price gets read on none of them.
          Unknown basis still warns, because 158 entries say nothing and
          silence is not the same as "flat".
        */}
        {costNeedsPartyCaveat(detail.costUsd, detail.costBasis, partySize) && (
          <p className="mt-2 text-xs text-bone-500">
            Costed for a party of {CATALOG_PARTY_SIZE}
            {partySize ? `, and you plan for ${partySize}` : ''} — treat it as a ceiling.
          </p>
        )}
      {/* Seasons only earn their space when they actually exclude something —
          "works in all four" is not worth a row of chips. */}
      {detail.seasons.length < 4 && (
        <Row label="best in">
          <span className="text-bone-300">{detail.seasons.join(', ')}</span>
        </Row>
      )}
      {detail.dayPart !== 'flexible' && (
        <Row label="time of day">
          <span className="text-bone-300">{detail.dayPart}</span>
        </Row>
      )}

      {detail.bookingNote && (
        <Row label="before you go">
          <span className="text-blaze-300">{detail.bookingNote}</span>
        </Row>
      )}

      {/*
        The curated pairings — the one piece of hand-written judgement in the
        catalog that had never been shown anywhere. Each is a button rather
        than a label because the useful response to "this goes with that" is to
        build the day that way, and making the reader re-find the other place
        in a 176-item grid would waste the information.
      */}
      {detail.pairs.length > 0 && (
        <section className="mt-6">
          <h3 className="numeric text-[11px] uppercase tracking-widest text-bone-500">goes well with</h3>
          <ul className="mt-2 space-y-1.5">
            {detail.pairs.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => onBuild(detail.id, p.id)}
                  className="w-full rounded-md bg-spruce-800 px-3 py-2 text-left text-sm transition-colors hover:bg-spruce-700"
                >
                  <span className="text-bone-100">{p.name}</span>
                  {p.note && <span className="block text-xs text-bone-500">{p.note}</span>}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/*
        Trail shape, when we have it. `effort` says how hard; this says how far
        and how much up, which is the difference between "moderate" meaning two
        miles and meaning nine. Route type matters because it decides whether
        the mileage is round trip.
      */}
      {(detail.lengthMiles !== null || detail.elevationGainFt !== null) && (
        <Row label="trail">
          <span className="text-bone-500">
            {[
              detail.lengthMiles !== null ? `${detail.lengthMiles} mi` : null,
              detail.elevationGainFt !== null
                ? `${detail.elevationGainFt.toLocaleString()} ft up`
                : null,
              detail.routeType !== null ? detail.routeType.replace(/_/g, ' ') : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </Row>
      )}

      {/*
        Audit tags are notes to whoever checks the catalog next — "this pin is
        deliberately approximate", "the drive really does end on gravel". Useful
        to an agent, meaningless beside "cheap" and "kid-favorite" to someone
        deciding on a Saturday.
      */}
      {detail.tags.filter((t) => !isAuditTag(t)).length > 0 && (
        <Row label="tags">
          <span className="text-bone-500">
            {detail.tags.filter((t) => !isAuditTag(t)).join(' · ')}
          </span>
        </Row>
      )}

      <Row label="checked">
        <span className={detail.status === 'verify' ? 'text-blaze-300' : 'text-bone-500'}>
          {detail.verifiedOn}
          {detail.status === 'verify' && ' — needs re-checking'}
        </span>
      </Row>

      {visits.length > 0 && (
        <Row label="been there">
          <span className="text-bone-300">
            {visits.length} {visits.length === 1 ? 'time' : 'times'}, last on {visits[0]!.visitedOn}
          </span>
        </Row>
      )}

      <div className="mt-6 flex gap-2">
        <button
          type="button"
          onClick={() => onBuild(detail.id)}
          className="flex-1 rounded-md bg-blaze-500 px-4 py-2.5 text-sm font-semibold text-spruce-950 transition-colors hover:bg-blaze-400"
        >
          Build the day around this
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md bg-spruce-800 px-4 py-2.5 text-sm text-bone-300 transition-colors hover:bg-spruce-700"
        >
          Close
        </button>
      </div>
    </div>
  );
}

function Fact({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-widest text-bone-500">{label}</dt>
      <dd className={accent ? 'text-blaze-400' : 'text-bone-100'}>{value}</dd>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mt-4 flex flex-col gap-0.5 sm:flex-row sm:gap-3">
      <span className="numeric shrink-0 text-[11px] uppercase leading-5 tracking-widest text-bone-500 sm:w-32">
        {label}
      </span>
      <span className="text-sm leading-5">{children}</span>
    </div>
  );
}
